import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Eye, EyeOff, Lock, Mail } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import toast from 'react-hot-toast'
import { Button, Card, Field, Input } from '../components/ui'

const loginSchema = z.object({
  email:    z.string().email('Please enter a valid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
})

type LoginForm = z.infer<typeof loginSchema>

export default function Login() {
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const { signIn } = useAuth()
  const navigate = useNavigate()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({ resolver: zodResolver(loginSchema) })

  const onSubmit = async (data: LoginForm) => {
    setIsLoading(true)
    try {
      await signIn({ email: data.email, password: data.password })
      toast.success('Successfully signed in!')
      navigate('/')
    } catch (error: any) {
      console.error('Login error:', error)
      toast.error(error.message || 'Failed to sign in')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-surface-base flex items-center justify-center p-4">
      {/* Decorative atmosphere — radial teal glow + warmer gold orb */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 [background:radial-gradient(60%_60%_at_15%_20%,rgb(var(--brand-teal)/0.18),transparent_60%),radial-gradient(45%_45%_at_85%_85%,rgb(var(--accent-gold)/0.14),transparent_60%)]"
      />

      <div className="relative w-full max-w-md">
        {/* Logo + Title */}
        <div className="text-center mb-8">
          <div className="mx-auto mb-5 h-16 w-16 rounded-2xl bg-gradient-to-br from-brand-bright to-brand-deep shadow-glow-teal flex items-center justify-center">
            <span className="font-display text-2xl text-white">SS</span>
          </div>
          <h1 className="font-display text-4xl text-ink leading-tight">SwipeSage Admin</h1>
          <p className="mt-2 text-sm text-ink-muted">
            Sign in to manage your credit card database
          </p>
        </div>

        {/* Login card */}
        <Card elevation="floating" className="p-8">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <Field
              label="Email address"
              required
              error={errors.email?.message}
            >
              <Input
                {...register('email')}
                type="email"
                autoComplete="email"
                placeholder="you@swipesage.com"
                leftIcon={<Mail size={18} />}
                invalid={!!errors.email}
              />
            </Field>

            <Field
              label="Password"
              required
              error={errors.password?.message}
            >
              <Input
                {...register('password')}
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="••••••••"
                leftIcon={<Lock size={18} />}
                invalid={!!errors.password}
                rightSlot={
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    className="p-1 rounded-md text-ink-subtle hover:text-ink transition-colors"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                }
              />
            </Field>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              loading={isLoading}
              className="w-full mt-2"
            >
              {isLoading ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>
        </Card>

        <p className="mt-8 text-center text-xs text-ink-subtle">
          © 2026 SwipeSage. All rights reserved.
        </p>
      </div>
    </div>
  )
}
